import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OTPVerificationModal = ({ 
  contactValue, 
  onVerify, 
  onClose,
  onResend 
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/?.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !otp?.[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.slice(0, 6);
    if (!/^\d+$/?.test(pastedData)) return;

    const newOtp = pastedData?.split('');
    setOtp([...newOtp, ...Array(6 - newOtp?.length)?.fill('')]);
    
    const lastIndex = Math.min(pastedData?.length - 1, 5);
    document.getElementById(`otp-${lastIndex}`)?.focus();
  };

  const handleVerify = () => {
    const otpValue = otp?.join('');
    if (otpValue?.length !== 6) {
      setError('Please enter complete 6-digit OTP');
      return;
    }

    if (otpValue === '123456') {
      onVerify(otpValue);
    } else {
      setError('Invalid OTP. Please try again or use 123456 for demo');
    }
  };

  const handleResend = () => {
    setOtp(['', '', '', '', '', '']);
    setError('');
    setCountdown(60);
    setCanResend(false);
    onResend();
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md bg-card rounded-xl shadow-warm-2xl animate-scale-in p-6 md:p-8">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-smooth"
        >
          <Icon name="X" size={20} />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="ShieldCheck" size={32} color="#FFFFFF" />
          </div>
          <h2 className="text-2xl font-heading font-semibold mb-2">Verify Your Email</h2>
          <p className="text-sm text-muted-foreground">
            We've sent a 6-digit code to
          </p>
          <p className="text-sm font-medium text-foreground mt-1">
            {contactValue}
          </p>
        </div>

        <div className="mb-6">
          <div className="flex gap-2 justify-center mb-2" onPaste={handlePaste}>
            {otp?.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e?.target?.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-12 md:w-14 md:h-14 text-center text-xl font-bold rounded-lg border-2 transition-smooth ${
                  error
                    ? 'border-error bg-error/5'
                    : digit
                    ? 'border-primary bg-primary/5' :'border-border bg-card'
                } focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary`}
              />
            ))}
          </div>
          {error && (
            <div className="flex items-center gap-2 text-error text-sm mt-2">
              <Icon name="AlertCircle" size={16} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="space-y-3 mb-6">
          <Button
            variant="default"
            fullWidth
            onClick={handleVerify}
            iconName="CheckCircle"
            iconPosition="left"
          >
            Verify OTP
          </Button>

          <div className="text-center">
            {canResend ? (
              <button
                onClick={handleResend}
                className="text-sm text-primary hover:underline font-medium"
              >
                Resend OTP
              </button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Resend OTP in <span className="font-mono font-medium text-foreground">{countdown}s</span>
              </p>
            )}
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5 text-primary" />
            <div className="text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Demo Credentials:</p>
              <p>Use OTP: <span className="font-mono font-medium text-foreground">123456</span></p>
              <p className="mt-1">Check your email inbox for the verification code</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationModal;